import { useAuth } from '@/contexts/AuthContext';
import { useDemoConfig } from '@/hooks/useDemoConfig';
import apiClient from '@/utils/apiClient';
import { TempDemoManager } from '@/utils/tempDemoManager';
import type {
  AgentInteractionState,
  ChatMessage,
  InteractionStep,
  OrchestratorState,
  ReasoningStep
} from '@demo-portal/shared';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AgentCard from './AgentCard';
import AgentCommunicationFlow from './AgentCommunicationFlow';
import AgentDiscoverySequence from './AgentDiscoverySequence';
import ChatInterface from './ChatInterface';
import InteractionFlowVisualizer from './InteractionFlowVisualizer';
import OrchestrationAgentsPanel from './OrchestrationAgentsPanel';
import OrchestrationControlPanel from './OrchestrationControlPanel';
import OrchestrationControls from './OrchestrationControls';
import OrchestrationDashboard from './OrchestrationDashboard';
import OrchestrationErrorBoundary from './OrchestrationErrorBoundary';
import OrchestrationFlowVisualizer from './OrchestrationFlowVisualizer';
import OrchestrationMetrics from './OrchestrationMetrics';
import OrchestrationStatusBar from './OrchestrationStatusBar';
import OrchestrationTimeline from './OrchestrationTimeline';
import type OrchestratorAgent from './OrchestratorAgent';

const DemoTemplate: React.FC = () => {
  const { demoId } = useParams<{ demoId: string }>();
  const { demoData, loading, error } = useDemoConfig(demoId || '');
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentReasoning, setCurrentReasoning] = useState<ReasoningStep[]>([]);
  const [activeAgents, setActiveAgents] = useState<string[]>([]);

  // Orchestration state
  const [orchestratorState, setOrchestratorState] = useState<OrchestratorState>({
    status: "initializing",
    discoveredAgents: [],
    currentStep: null,
    activeAgents: [],
    processingQueue: [],
  });
  const [agentInteractions, setAgentInteractions] = useState<Map<string, AgentInteractionState>>(new Map());
  const [currentInteractionFlow, setCurrentInteractionFlow] = useState<InteractionStep[]>([]);
  const [initializationComplete, setInitializationComplete] = useState(false);
  const [currentInitStep, setCurrentInitStep] = useState<string>("");

  // Check if this is a temporary demo
  const isTemporaryDemo = demoId ? TempDemoManager.isTemporaryId(demoId) : false;

  // Check if orchestration mode is enabled
  const isOrchestrationMode = demoData?.config?.orchestrationMode || false;

  // State for save demo functionality
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showFloatingSave, setShowFloatingSave] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  // Clean up temporary demo from session storage when component unmounts
  useEffect(() => {
    return () => {
      if (isTemporaryDemo && demoId) {
        // Clean up the temporary demo when leaving the page
        TempDemoManager.remove(demoId);
      }
    };
  }, [isTemporaryDemo, demoId]);

  // Handle scroll to show/hide floating save button
  useEffect(() => {
    if (!isTemporaryDemo || saveSuccess) return;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const shouldShow = scrollY > 300; // Show after scrolling 300px
      setShowFloatingSave(shouldShow);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isTemporaryDemo, saveSuccess]);

  // Initialize orchestration mode
  useEffect(() => {
    if (isOrchestrationMode && demoData && !initializationComplete) {
      setOrchestratorState(prev => ({
        ...prev,
        status: "initializing",
      }));
    }
  }, [isOrchestrationMode, demoData, initializationComplete]);

  // Orchestration handlers
  const handleDiscoveryComplete = useCallback((discoveredAgents: string[]) => {
    setOrchestratorState(prev => ({
      ...prev,
      status: "ready",
      discoveredAgents,
    }));
    setInitializationComplete(true);
  }, []);

  const handleInitializationStep = useCallback((step: string) => {
    setCurrentInitStep(step);
    if (step.includes("Discovering")) {
      setOrchestratorState(prev => ({
        ...prev,
        status: "discovering",
      }));
    }
  }, []);

  const handleInteractionStepComplete = useCallback((step: InteractionStep) => {
    setOrchestratorState(prev => ({
      ...prev,
      currentStep: step,
    }));
  }, []);

  const handleInteractionFlowComplete = useCallback(() => {
    setOrchestratorState(prev => ({
      ...prev,
      status: "ready",
      currentStep: null,
      processingQueue: [],
    }));
  }, []);

  // Enhanced orchestration flow execution with detailed progress tracking
  const executeOrchestrationFlow = useCallback(async (example: any, userMessage: string) => {
    if (!demoData?.orchestrator) return;

    // Initialize all agents as idle
    const agentCallOrder = example.orchestrationFlow?.agentCallOrder || [];
    setAgentInteractions(prev => {
      const newMap = new Map(prev);
      demoData.agents.forEach(agent => {
        newMap.set(agent.id, {
          agentId: agent.id,
          status: 'idle',
          currentAction: null,
          toolsInUse: [],
          progress: 0,
        });
      });
      return newMap;
    });

    // Phase 1: Orchestrator Analysis
    setOrchestratorState(prev => ({
      ...prev,
      status: "processing",
      currentStep: {
        stepId: 'orchestrator_analysis',
        agent: 'orchestrator',
        action: 'analyze',
        description: example.orchestrationFlow?.orchestratorAnalysis || 'Analyzing request and determining coordination strategy',
        duration: demoData.config.interactionDelays?.orchestratorThinking || 1200,
        dependencies: [],
      },
      activeAgents: ['orchestrator'],
      processingQueue: agentCallOrder.map((agentId, index) => ({
        stepId: `agent_call_${index + 1}`,
        agent: agentId,
        action: 'call_agent' as const,
        description: `Process with ${demoData.agents.find(a => a.id === agentId)?.name || agentId}`,
        duration: demoData.config.interactionDelays?.agentProcessing || 1500,
        dependencies: [],
      })),
    }));

    // Simulate orchestrator thinking with visual feedback
    const thinkingTime = demoData.config.interactionDelays?.orchestratorThinking || 1200;
    const thinkingSteps = 3;
    const thinkingDelay = thinkingTime / thinkingSteps;

    for (let step = 1; step <= thinkingSteps; step++) {
      await new Promise(resolve => setTimeout(resolve, thinkingDelay));

      setOrchestratorState(prev => ({
        ...prev,
        currentStep: prev.currentStep ? {
          ...prev.currentStep,
          description: `${example.orchestrationFlow?.orchestratorAnalysis || 'Analyzing request'} (${step}/${thinkingSteps})`,
        } : null,
      }));
    }

    // Phase 2: Agent Coordination with Enhanced Progress Tracking
    for (let i = 0; i < agentCallOrder.length; i++) {
      const agentId = agentCallOrder[i];
      const agent = demoData.agents.find(a => a.id === agentId);
      const agentAction = example.orchestrationFlow?.agentActions?.[agentId];

      if (agent && agentAction) {
        // Mark agent as called
        setAgentInteractions(prev => {
          const newMap = new Map(prev);
          newMap.set(agentId, {
            agentId,
            status: 'called',
            currentAction: agentAction.action,
            toolsInUse: agentAction.tools || [],
            progress: 0,
          });
          return newMap;
        });

        // Update orchestrator state with current step
        setOrchestratorState(prev => ({
          ...prev,
          currentStep: {
            stepId: `agent_call_${i + 1}`,
            agent: agentId,
            action: 'call_agent',
            description: `Coordinating with ${agent.name}: ${agentAction.reasoning}`,
            duration: demoData.config.interactionDelays?.agentProcessing || 1500,
            dependencies: [],
          },
          activeAgents: [agentId],
          processingQueue: prev.processingQueue.slice(1), // Remove current step from queue
        }));

        // Brief delay for call initiation
        await new Promise(resolve => setTimeout(resolve, 200));

        // Simulate detailed agent processing with multiple phases
        const processingTime = demoData.config.interactionDelays?.agentProcessing || 1500;
        const phases = [
          { name: 'initializing', duration: 0.2, status: 'called' as const },
          { name: 'processing', duration: 0.6, status: 'processing' as const },
          { name: 'responding', duration: 0.2, status: 'responding' as const },
        ];

        let totalProgress = 0;

        for (const phase of phases) {
          const phaseDuration = processingTime * phase.duration;
          const phaseSteps = Math.max(3, Math.floor(phaseDuration / 100)); // At least 3 steps per phase
          const stepDelay = phaseDuration / phaseSteps;

          // Update agent status for this phase
          setAgentInteractions(prev => {
            const newMap = new Map(prev);
            const current = newMap.get(agentId);
            if (current) {
              newMap.set(agentId, {
                ...current,
                status: phase.status,
                currentAction: `${phase.name}: ${agentAction.action}`,
              });
            }
            return newMap;
          });

          // Animate progress through this phase
          for (let step = 1; step <= phaseSteps; step++) {
            await new Promise(resolve => setTimeout(resolve, stepDelay));

            const phaseProgress = (step / phaseSteps) * phase.duration * 100;
            const newTotalProgress = totalProgress + phaseProgress;

            setAgentInteractions(prev => {
              const newMap = new Map(prev);
              const current = newMap.get(agentId);
              if (current) {
                newMap.set(agentId, {
                  ...current,
                  progress: Math.min(95, newTotalProgress), // Cap at 95% until complete
                });
              }
              return newMap;
            });
          }

          totalProgress += phase.duration * 100;
        }

        // Mark agent as complete
        setAgentInteractions(prev => {
          const newMap = new Map(prev);
          newMap.set(agentId, {
            agentId,
            status: 'complete',
            currentAction: 'completed',
            toolsInUse: [],
            progress: 100,
          });
          return newMap;
        });

        // Brief pause between agents for visual clarity
        await new Promise(resolve => setTimeout(resolve, 400));
      }
    }

    // Phase 3: Response Synthesis with Progress
    setOrchestratorState(prev => ({
      ...prev,
      status: "synthesizing",
      currentStep: {
        stepId: 'response_synthesis',
        agent: 'orchestrator',
        action: 'synthesize',
        description: example.orchestrationFlow?.synthesisProcess || 'Compiling agent responses into comprehensive answer',
        duration: demoData.config.interactionDelays?.responseCompilation || 1000,
        dependencies: [],
      },
      activeAgents: ['orchestrator'],
      processingQueue: [],
    }));

    // Simulate synthesis with progress steps
    const synthesisTime = demoData.config.interactionDelays?.responseCompilation || 1000;
    const synthesisSteps = 4;
    const synthesisDelay = synthesisTime / synthesisSteps;

    const synthesisPhases = [
      'Collecting agent responses',
      'Analyzing response quality',
      'Synthesizing comprehensive answer',
      'Finalizing response format',
    ];

    for (let step = 0; step < synthesisSteps; step++) {
      await new Promise(resolve => setTimeout(resolve, synthesisDelay));

      setOrchestratorState(prev => ({
        ...prev,
        currentStep: prev.currentStep ? {
          ...prev.currentStep,
          description: synthesisPhases[step] || prev.currentStep.description,
        } : null,
      }));
    }

    // Complete orchestration
    setOrchestratorState(prev => ({
      ...prev,
      status: "ready",
      currentStep: null,
      activeAgents: [],
      processingQueue: [],
    }));
  }, [demoData]);

  const simulateAgentResponse = useCallback(async (userMessage: string) => {
    if (!demoData) return;

    setIsProcessing(true);
    setCurrentReasoning([]);
    setActiveAgents([]);

    // Find matching example or use default response
    const matchingExample = demoData.examples.find(example =>
      userMessage.toLowerCase().includes(example.category.toLowerCase()) ||
      example.query.toLowerCase().includes(userMessage.toLowerCase().split(' ')[0])
    );

    const responseContent = matchingExample?.mockResponse ||
      demoData.responseTemplates?.default ||
      generateFallbackResponse(userMessage, demoData.config.industry);

    if (isOrchestrationMode && demoData.orchestrator && matchingExample?.orchestrationFlow) {
      // Orchestration mode - enhanced interaction flow
      await executeOrchestrationFlow(matchingExample, userMessage);
    } else {
      // Legacy mode - simulate reasoning steps
      const mockReasoningSteps: ReasoningStep[] = [
        {
          agent: `${demoData.agents[0].name}`,
          step: "Request Analysis",
          reasoning: `Analyzing user request: "${userMessage}". Evaluating complexity and determining which specialized agents should be involved in the response.`,
          decision: "Request classified as multi-agent coordination required. Routing to specialized agents for comprehensive analysis.",
          timestamp: Date.now(),
          avatar: demoData.agents[0].avatar,
        },
      ];

      // Add reasoning for other relevant agents
      const relevantAgents = matchingExample?.expectedAgents || ['orchestrator'];
      const agentsToUse = demoData.agents.filter(agent =>
        relevantAgents.includes(agent.id) || relevantAgents.some(expected =>
          agent.name.toLowerCase().includes(expected.toLowerCase())
        )
      ).slice(1, 3);

      agentsToUse.forEach((agent, index) => {
        mockReasoningSteps.push({
          agent: agent.name,
          step: `${agent.role} Analysis`,
          reasoning: `Professional evaluation from ${agent.role.toLowerCase()} perspective. Analyzing requirements and providing specialized insights based on expertise in ${agent.expertise.join(', ')}.`,
          decision: `Recommendations provided based on ${agent.dataSources.length} data sources and ${agent.tools.length} specialized tools. Analysis complete.`,
          timestamp: Date.now() + (index + 1) * 1000,
          avatar: agent.avatar,
        });
      });

      // Display reasoning steps with timing
      for (let i = 0; i < mockReasoningSteps.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 1000));

        setCurrentReasoning(prev => [...prev, mockReasoningSteps[i]]);
        const agentIndex = i < demoData.agents.length ? i : 0;
        setActiveAgents(prev => [...prev, demoData.agents[agentIndex].id]);
      }

      // Generate response after reasoning
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    const responseMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'agent',
      content: responseContent,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, responseMessage]);
    setIsProcessing(false);

    if (isOrchestrationMode) {
      setOrchestratorState(prev => ({
        ...prev,
        status: "ready",
      }));
    }
  }, [demoData, isOrchestrationMode, orchestratorState.status]);

  const handleSendMessage = useCallback(async (message: string) => {
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    await simulateAgentResponse(message);
  }, [simulateAgentResponse]);

  const generateFallbackResponse = (userMessage: string, industry: string): string => {
    const responses = {
      automotive: `**Multi-Agent Analysis Complete**

Thank you for your inquiry regarding automotive certification requirements. Our specialized agents have analyzed your request and provide the following comprehensive assessment:

**Quality Standards Assessment:**
- ISO/TS 16949 certification pathway identified
- Estimated timeline: 12-16 weeks for complete certification
- Key requirements: Process validation, supplier qualification, documentation systems

**Materials & Testing Requirements:**
- Aluminum die-casting process validation required
- Mechanical testing: Tensile strength, fatigue resistance, corrosion testing
- Chemical analysis: Alloy composition verification

**Regulatory Compliance:**
- BMW supplier requirements alignment confirmed
- Global automotive standards compliance verified
- Documentation package preparation required

**Risk Assessment:**
- Supply chain risks: Low to moderate
- Technical risks: Manageable with proper planning
- Timeline risks: Mitigated through phased approach

**Next Steps:**
1. Detailed process audit and gap analysis
2. Testing protocol development and validation
3. Documentation system implementation
4. Final certification audit scheduling

Our agents are ready to support you through each phase of the certification process.`,

      fragrance: `**Fragrance Development Analysis Complete**

Our fragrance development team has analyzed your brief and presents the following comprehensive development plan:

**Creative Concept - Master Perfumer:**
- Fragrance Profile: Modern Botanical Luxury
- Olfactory Structure: Fresh-floral opening, sophisticated heart, warm sensual base
- Key Ingredients: Sustainable bergamot, jasmine sambac, sandalwood accord
- Consumer Appeal: Premium unisex positioning for sophisticated consumers

**Regulatory Compliance Assessment:**
- IFRA Compliance: Fully compliant formulation
- Global Market Access: EU, US, Asia-Pacific approved
- Allergen Declaration: Standard labeling requirements
- Safety Assessment: Complete toxicological evaluation

**Supply Chain & Cost Analysis:**
- Production Cost: €58/kg (within target range)
- Sustainable Sourcing: 85% sustainably sourced ingredients
- Lead Time: 4-6 weeks for production quantities
- Supply Risk: Low - diversified supplier network

**Project Timeline:**
- Concept Development: 2 weeks
- Formulation & Testing: 6 weeks  
- Regulatory Approval: 4 weeks
- Production Scale-up: 4 weeks
- Total Timeline: 16 weeks

**Commercial Viability: Excellent**
All technical and commercial requirements met. Ready to proceed with detailed formulation development.`,
    };

    return responses[industry as keyof typeof responses] ||
      "Thank you for your inquiry. Our multi-agent system has analyzed your request and will provide detailed recommendations based on our specialized expertise.";
  };

  const handleSaveDemo = useCallback(async (skipConfirmation = false) => {
    if (!demoData || !user) return;

    // Show confirmation dialog for first-time saves
    if (!skipConfirmation && !showSaveConfirmation) {
      setShowSaveConfirmation(true);
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    setShowSaveConfirmation(false);

    try {
      // Create a permanent demo from the temporary configuration
      const saveData = {
        userId: user.username || user.email,
        title: demoData.config.title,
        description: demoData.config.description,
        industry: demoData.config.industry,
        businessDomain: demoData.config.businessDomain,
        config: demoData.config,
        agents: demoData.agents,
        examples: demoData.examples,
        // Add additional metadata for better organization
        initialPrompt: demoData.config.scenario?.description || '',
        prompt: demoData.config.scenario?.description || '',
      };

      console.log('Saving demo with data:', {
        ...saveData,
        config: saveData.config ? 'Present' : 'Missing',
        agents: saveData.agents ? `${Array.isArray(saveData.agents) ? saveData.agents.length : 'Object'} agents` : 'Missing',
        examples: saveData.examples ? `${Array.isArray(saveData.examples) ? saveData.examples.length : 'Object'} examples` : 'Missing'
      });

      const response = await apiClient.post('/demo', saveData);

      if (response.error) {
        // Provide more specific error messages
        if (response.error.includes('authentication') || response.error.includes('token')) {
          throw new Error('Your session has expired. Please refresh the page and try again.');
        } else if (response.error.includes('network') || response.error.includes('fetch')) {
          throw new Error('Network error. Please check your connection and try again.');
        } else {
          throw new Error(response.error);
        }
      }

      // Check for successful response (2xx status codes)
      if (response.status >= 200 && response.status < 300 && response.data?.id) {
        setSaveSuccess(true);

        // Clean up the temporary demo from session storage
        if (isTemporaryDemo && demoId) {
          TempDemoManager.remove(demoId);
        }

        // Navigate to the permanent demo after a short delay
        setTimeout(() => {
          navigate(`/demo/${response.data.id}`);
        }, 2000);
      } else {
        throw new Error('Failed to save demo - invalid response from server');
      }
    } catch (error) {
      console.error('Error saving demo:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save demo');
    } finally {
      setIsSaving(false);
    }
  }, [demoData, user, navigate, isTemporaryDemo, demoId, showSaveConfirmation]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f7fa'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '3em',
            marginBottom: '20px',
            animation: 'spin 1s linear infinite'
          }}>
            🤖
          </div>
          <p style={{ fontSize: '1.2em', color: '#666' }}>Loading demo configuration...</p>
        </div>
      </div>
    );
  }

  if (error || !demoData) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f7fa'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '500px', padding: '40px' }}>
          <div style={{ fontSize: '3em', marginBottom: '20px' }}>❌</div>
          <h2 style={{ color: '#e74c3c', marginBottom: '15px' }}>Demo Not Found</h2>
          <p style={{ color: '#666', marginBottom: '30px' }}>
            {error || 'The requested demo could not be loaded.'}
          </p>
          <Link
            to="/"
            style={{
              display: 'inline-block',
              background: '#3498db',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: 'bold',
            }}
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const { config, agents, examples, welcomeMessage } = demoData;

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      {/* Authentication Header */}
      <div style={{
        background: 'rgba(0,0,0,0.05)',
        padding: '10px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '14px',
        color: '#666'
      }}>
        <div>
          <Link to="/" style={{ color: '#667eea', textDecoration: 'none', marginRight: '20px' }}>
            ← Back to Portal
          </Link>
          <span>Signed in as {user?.username || user?.email}</span>
        </div>
        <button
          onClick={signOut}
          style={{
            background: 'transparent',
            border: '1px solid #ddd',
            color: '#666',
            padding: '6px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
          }}
        >
          Sign Out
        </button>
      </div>

      {/* Temporary Demo Banner */}
      {isTemporaryDemo && (
        <div style={{
          background: saveSuccess
            ? 'linear-gradient(135deg, #2ecc71, #27ae60)'
            : 'linear-gradient(135deg, #ff9a56, #ff6b6b)',
          color: 'white',
          padding: '14px 20px',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: '500',
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Animated background for saved state */}
          {saveSuccess && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
              animation: 'shimmer 2s ease-in-out infinite'
            }} />
          )}

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {saveSuccess ? (
                <>
                  <span style={{ fontSize: '16px' }}>✅</span>
                  <span>Demo saved successfully! Redirecting to permanent version...</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: '16px' }}>⏰</span>
                  <span>Temporary demo - expires in 1 hour</span>
                </>
              )}
            </div>

            {!saveSuccess && (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  onClick={() => handleSaveDemo()}
                  disabled={isSaving}
                  style={{
                    background: isSaving
                      ? 'rgba(255,255,255,0.3)'
                      : 'rgba(255,255,255,0.95)',
                    border: 'none',
                    color: isSaving ? 'white' : '#ff6b6b',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: isSaving ? 0.8 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isSaving) {
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSaving) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.95)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {isSaving ? (
                    <>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Saving...
                    </>
                  ) : (
                    <>💾 Save Demo</>
                  )}
                </button>

                <button
                  onClick={() => navigate('/generate-demo')}
                  disabled={isSaving}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    color: 'white',
                    padding: '8px 14px',
                    borderRadius: '6px',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    opacity: isSaving ? 0.5 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSaving) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSaving) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                    }
                  }}
                >
                  ⚡ New Demo
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{
        background: config.uiConfig.headerGradient,
        color: 'white',
        padding: '25px',
        textAlign: 'center',
      }}>
        <h1 style={{ fontSize: '2.8em', marginBottom: '10px' }}>
          {config.uiConfig.headerIcon} {config.title}
        </h1>
        <p style={{ fontSize: '1.3em', opacity: 0.9 }}>
          {config.description}
        </p>
      </div>

      {/* Orchestration Status Bar */}
      {isOrchestrationMode && initializationComplete && (
        <OrchestrationStatusBar
          orchestratorState={orchestratorState}
          agentInteractions={agentInteractions}
          isProcessing={isProcessing}
          primaryColor={config.uiConfig.primaryColor}
        />
      )}

      {/* Main Content */}
      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '20px' }}>
        {/* Scenario Info */}
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '20px',
        }}>
          <div style={{ fontWeight: 'bold', color: '#856404', marginBottom: '10px' }}>
            {config.scenario.icon} Scenario: {config.scenario.title}
          </div>
          <div style={{ color: '#856404' }}>
            {config.scenario.description}
          </div>
        </div>

        {/* Demo Layout */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 400px',
          gap: '20px',
          marginTop: '20px',
          height: 'calc(100vh - 300px)', // Constrain total height
          minHeight: '600px',
        }}>
          {/* Chat Panel */}
          <ChatInterface
            messages={messages}
            isProcessing={isProcessing}
            examples={examples}
            welcomeMessage={welcomeMessage || ''}
            onSendMessage={handleSendMessage}
            primaryColor={config.uiConfig.primaryColor}
          />

          {/* Agents Panel */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            height: '100%',
          }}>
            {/* Save Demo Button for Temporary Demos */}
            {isTemporaryDemo && (
              <div data-save-demo-button style={{
                background: saveSuccess
                  ? 'linear-gradient(135deg, #2ecc71, #27ae60)'
                  : saveError
                    ? 'linear-gradient(135deg, #e74c3c, #c0392b)'
                    : 'linear-gradient(135deg, #667eea, #764ba2)',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                border: '1px solid rgba(255,255,255,0.2)',
              }}>
                <div style={{
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  {saveSuccess ? (
                    <>
                      <span style={{ fontSize: '20px' }}>✅</span>
                      Demo saved successfully! Redirecting...
                    </>
                  ) : saveError ? (
                    <>
                      <span style={{ fontSize: '20px' }}>❌</span>
                      {saveError}
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '20px' }}>💾</span>
                      Save this demo to your collection
                    </>
                  )}
                </div>

                {!saveSuccess && !saveError && (
                  <div style={{
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '13px',
                    marginBottom: '16px',
                    lineHeight: '1.4'
                  }}>
                    This temporary demo will expire in 1 hour. Save it to access it anytime!
                  </div>
                )}

                {!saveSuccess && (
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleSaveDemo()}
                      disabled={isSaving}
                      style={{
                        background: 'white',
                        color: saveError ? '#e74c3c' : '#667eea',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        opacity: isSaving ? 0.7 : 1,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        minWidth: '120px',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSaving) {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSaving) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                        }
                      }}
                    >
                      {isSaving ? (
                        <>
                          <div style={{
                            width: '14px',
                            height: '14px',
                            border: '2px solid #e0e7ff',
                            borderTop: '2px solid #667eea',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                          Saving...
                        </>
                      ) : saveError ? (
                        <>🔄 Retry Save</>
                      ) : (
                        <>💾 Save Demo</>
                      )}
                    </button>

                    <button
                      onClick={() => navigate('/generate-demo')}
                      disabled={isSaving}
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        fontWeight: '500',
                        fontSize: '14px',
                        opacity: isSaving ? 0.5 : 1,
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSaving) {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSaving) {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                        }
                      }}
                    >
                      ⚡ New Demo
                    </button>
                  </div>
                )}

                {saveError && (
                  <div style={{
                    marginTop: '12px',
                    padding: '10px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.9)',
                    lineHeight: '1.4'
                  }}>
                    💡 Tip: Make sure you're connected to the internet and try again. If the problem persists, try generating a new demo.
                  </div>
                )}
              </div>
            )}

            {/* Orchestration Mode Components */}
            {isOrchestrationMode && demoData.orchestrator ? (
              <OrchestrationErrorBoundary>
                {/* Agent Discovery Sequence */}
                {!initializationComplete && (
                  <AgentDiscoverySequence
                    orchestrator={demoData.orchestrator}
                    agents={agents}
                    discoveryDelay={demoData.config.agentDiscoveryDelay}
                    onDiscoveryComplete={handleDiscoveryComplete}
                    onInitializationStep={handleInitializationStep}
                  />
                )}

                {/* Orchestration Dashboard */}
                {initializationComplete && (
                  <OrchestrationDashboard
                    orchestratorState={orchestratorState}
                    agentInteractions={agentInteractions}
                    agents={agents}
                    orchestrator={demoData.orchestrator}
                    currentStep={orchestratorState.currentStep}
                    primaryColor={config.uiConfig.primaryColor}
                  />
                )}

                {/* Agent Communication Flow */}
                {initializationComplete && (
                  <AgentCommunicationFlow
                    orchestrator={demoData.orchestrator}
                    agents={agents}
                    currentStep={orchestratorState.currentStep}
                    activeAgents={orchestratorState.activeAgents}
                    primaryColor={config.uiConfig.primaryColor}
                  />
                )}

                {/* Orchestration Flow Visualizer */}
                {initializationComplete && (
                  <OrchestrationFlowVisualizer
                    orchestrator={demoData.orchestrator}
                    agents={agents}
                    orchestratorState={orchestratorState}
                    agentInteractions={agentInteractions}
                    primaryColor={config.uiConfig.primaryColor}
                  />
                )}

                {/* Orchestration Timeline */}
                {initializationComplete && (
                  <OrchestrationTimeline
                    orchestrator={demoData.orchestrator}
                    agents={agents}
                    orchestratorState={orchestratorState}
                    agentInteractions={agentInteractions}
                    primaryColor={config.uiConfig.primaryColor}
                  />
                )}

                {/* Orchestration Metrics */}
                {initializationComplete && (
                  <OrchestrationMetrics
                    orchestrator={demoData.orchestrator}
                    agents={agents}
                    orchestratorState={orchestratorState}
                    agentInteractions={agentInteractions}
                    primaryColor={config.uiConfig.primaryColor}
                  />
                )}

                {/* Orchestration Control Panel */}
                {initializationComplete && (
                  <OrchestrationControlPanel
                    orchestrator={demoData.orchestrator}
                    agents={agents}
                    orchestratorState={orchestratorState}
                    agentInteractions={agentInteractions}
                    primaryColor={config.uiConfig.primaryColor}
                  />
                )}

                {/* Interaction Flow Visualizer */}
                {currentInteractionFlow.length > 0 && isProcessing && (
                  <InteractionFlowVisualizer
                    interactionFlow={currentInteractionFlow}
                    orchestrator={demoData.orchestrator}
                    agents={agents}
                    isActive={isProcessing}
                    onStepComplete={handleInteractionStepComplete}
                    onFlowComplete={handleInteractionFlowComplete}
                    primaryColor={config.uiConfig.primaryColor}
                  />
                )}

                {/* Orchestrator Agent Display */}
                {initializationComplete && (
                  <OrchestratorAgent
                    orchestrator={demoData.orchestrator}
                    state={orchestratorState}
                    primaryColor={config.uiConfig.primaryColor}
                  />
                )}

                {/* Enhanced Specialized Agents Panel with Orchestration Features */}
                {initializationComplete && (
                  <OrchestrationAgentsPanel
                    agents={agents}
                    agentInteractions={agentInteractions}
                    orchestratorState={orchestratorState}
                    currentReasoning={currentReasoning}
                    activeAgents={activeAgents}
                    primaryColor={config.uiConfig.primaryColor}
                  />
                )}
              </OrchestrationErrorBoundary>
            ) : (
              /* Legacy Mode - Original Agents Panel */
              <div style={{
                background: 'white',
                borderRadius: '15px',
                padding: '20px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}>
                <div style={{
                  fontSize: '1.4em',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  color: '#2c3e50',
                  borderBottom: `2px solid ${config.uiConfig.primaryColor}`,
                  paddingBottom: '8px',
                  flexShrink: 0,
                }}>
                  🤖 AI Agents
                </div>

                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  paddingRight: '5px',
                  marginRight: '-5px',
                }}>
                  {agents.map((agent) => {
                    const reasoning = currentReasoning.find(r =>
                      r.agent.includes(agent.name.split(' ')[0]) ||
                      r.agent.includes(agent.name.split(' ')[1])
                    );

                    return (
                      <AgentCard
                        key={agent.id}
                        agent={agent}
                        isActive={activeAgents.includes(agent.id)}
                        reasoning={reasoning}
                        primaryColor={config.uiConfig.primaryColor}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating Save Button */}
      {isTemporaryDemo && showFloatingSave && !saveSuccess && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 1000,
          transform: showFloatingSave ? 'translateY(0)' : 'translateY(100px)',
          opacity: showFloatingSave ? 1 : 0,
          transition: 'all 0.3s ease-in-out',
        }}>
          <button
            onClick={() => handleSaveDemo()}
            disabled={isSaving}
            style={{
              background: saveError
                ? 'linear-gradient(135deg, #e74c3c, #c0392b)'
                : 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              border: 'none',
              borderRadius: '50px',
              padding: '16px 24px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              opacity: isSaving ? 0.8 : 1,
              transition: 'all 0.2s ease',
              minWidth: '160px',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 35px rgba(0,0,0,0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.3)';
              }
            }}
          >
            {isSaving ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Saving...
              </>
            ) : saveError ? (
              <>🔄 Retry Save</>
            ) : (
              <>💾 Save Demo</>
            )}
          </button>
        </div>
      )}

      {/* Save Confirmation Dialog */}
      {showSaveConfirmation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '30px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>💾</div>
            <h3 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#2c3e50',
              marginBottom: '15px'
            }}>
              Save Demo to Your Collection?
            </h3>
            <p style={{
              fontSize: '16px',
              color: '#666',
              lineHeight: '1.5',
              marginBottom: '25px'
            }}>
              This will create a permanent copy of "{demoData?.config.title}" that you can access anytime.
              The temporary version will be removed.
            </p>

            <div style={{
              background: '#f8f9fa',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '25px',
              textAlign: 'left'
            }}>
              <div style={{ fontSize: '14px', color: '#495057', marginBottom: '8px' }}>
                <strong>Demo Details:</strong>
              </div>
              <div style={{ fontSize: '13px', color: '#6c757d', lineHeight: '1.4' }}>
                • Title: {demoData?.config.title}<br />
                • Industry: {demoData?.config.industry}<br />
                • Agents: {demoData?.agents.length} specialized agents<br />
                • Examples: {demoData?.examples.length} interaction examples
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowSaveConfirmation(false)}
                style={{
                  background: 'transparent',
                  border: '2px solid #e9ecef',
                  color: '#6c757d',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#adb5bd';
                  e.currentTarget.style.color = '#495057';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e9ecef';
                  e.currentTarget.style.color = '#6c757d';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveDemo(true)}
                style={{
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  border: 'none',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
              >
                💾 Save Demo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Orchestration Controls - Floating Action Button */}
      {isOrchestrationMode && initializationComplete && (
        <OrchestrationControls
          orchestratorState={orchestratorState}
          isProcessing={isProcessing}
          onReset={() => {
            setOrchestratorState({
              status: "ready",
              discoveredAgents: orchestratorState.discoveredAgents,
              currentStep: null,
              activeAgents: [],
              processingQueue: [],
            });
            setAgentInteractions(new Map());
            setCurrentInteractionFlow([]);
            setCurrentReasoning([]);
            setActiveAgents([]);
          }}
          primaryColor={demoData?.config?.uiConfig?.primaryColor}
        />
      )}
    </div>
  );
};

export default DemoTemplate;